import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState, useRef, useEffect } from 'react';
// Import shared ticket status helpers from common CRM constants
import { getTicketStatusLabel, getTicketStatusColor, isTicketOpen } from './crmConstants';
import StitchLayout from '../../components/Crm/CrmLayout';
// Reuse existing call and ticket detail modal components
import OzonetelCallDetails from '../../components/Crm/OzonetelCallDetails';
import SaharaCallDetails from '../../components/Crm/SaharaCallDetails';
import { Input, Button, Spin, Empty, Tag, Tooltip, Avatar, Segmented } from 'antd';
import WhatsAppConversationModal from '../../components/Crm/WhatsAppConversationModal';
import {
    SearchOutlined,
    PhoneOutlined,
    ShoppingOutlined,
    CustomerServiceOutlined,
    RobotOutlined,
    PlayCircleOutlined,
    UserOutlined,
    ClockCircleOutlined,
    ThunderboltOutlined,
    MailOutlined,
    IdcardOutlined,
} from '@ant-design/icons';
import ItemHistoryModal, { ItemHistoryBadge } from '../../components/Crm/ItemHistoryModal';
import moment from 'moment';

// Order item shape extracted from raw API data
interface OrderItem {
    orderId: string;
    itemId: string;
    productName: string;
    productImageUrl: string;
    price: string;
    paymentMode: string;
    itemStatus: string;
    orderCreationDate: string;
    estimatedDelivery: string;
}

// Unified interaction event (calls + tickets only)
interface InteractionEvent {
    id: string;
    type: 'ivr_call' | 'sahara_call' | 'ticket';
    time: string;
    title: string;
    subtitle: string;
    meta: Record<string, any>;
}

// Extract flat order item list from nested API orders
const buildOrders = (data: any): OrderItem[] => {
    const items: OrderItem[] = [];
    (data.orders || []).forEach((order: any) => {
        (order.items || []).forEach((item: any) => {
            items.push({
                orderId: item.orderId,
                itemId: item.itemId,
                productName: item.productName,
                productImageUrl: item.productImageUrl,
                price: `₹${item.price}`,
                paymentMode: item.paymentMode,
                itemStatus: item.itemStatus,
                orderCreationDate: item.orderCreationDate,
                estimatedDelivery: item.estimatedDelivery,
            });
        });
    });
    items.sort((a, b) => moment(b.orderCreationDate).valueOf() - moment(a.orderCreationDate).valueOf());
    return items;
};

// Build time-sorted interaction events (calls + tickets only)
const buildInteractions = (data: any): InteractionEvent[] => {
    const events: InteractionEvent[] = [];

    (data.ivr_call_history || []).forEach((call: any) => {
        events.push({
            id: `ivr-${call.UCID}`,
            type: 'ivr_call',
            time: call.StartTime,
            title: `IVR Call — ${call.Status}`,
            subtitle: call.Disposition || 'No Disposition',
            meta: {
                agent: call.Agent,
                duration: call.CallDuration,
                hangupBy: call.HangupBy,
                audioUrl: call.AudioFile,
            },
        });
    });

    (data.sahara_call_history || []).forEach((call: any) => {
        events.push({
            id: `sahara-${call.ucid}`,
            type: 'sahara_call',
            time: call.start_time,
            title: `Voice AI Call`,
            subtitle: `UCID: ${call.ucid}`,
            meta: {
                turnCount: call?.metrics?.turn_count,
                toolUseCount: call?.metrics?.tool_use_count,
            },
        });
    });

    (data.tickets || []).forEach((ticket: any) => {
        // Derive department label based on source field
        const source = ticket.source;
        let department: string | null = null;
        if (source === 3) {
            department = 'IVR';
        } else if (source === 2) {
            department = ticket.custom_fields?.cf_department || null;
        }
        events.push({
            id: `ticket-${ticket.id}`,
            type: 'ticket',
            time: ticket.created_at,
            title: `Ticket #${ticket.id}`,
            subtitle: ticket.subject || ticket.custom_fields?.cf_concern_1 || 'Support Request',
            meta: {
                status: ticket.status,
                category: ticket.custom_fields?.cf_category,
                agent: ticket.custom_fields?.cf_agent_name,
                department,
            },
        });
    });

    events.sort((a, b) => moment(b.time).valueOf() - moment(a.time).valueOf());
    return events;
};

// Visual config per interaction event type
const TYPE_CONFIG = {
    ivr_call: { icon: <PhoneOutlined />, color: '#3b82f6', label: 'IVR Call', labelColor: 'blue' as any },
    sahara_call: { icon: <RobotOutlined />, color: '#8b5cf6', label: 'Voice AI', labelColor: 'purple' as any },
    ticket: { icon: <CustomerServiceOutlined />, color: '#f59e0b', label: 'Ticket', labelColor: 'gold' as any },
};

// Single order card for the left panel — uses shared ItemHistoryBadge trigger

const OrderCard = ({ item, onItemClick }: { item: OrderItem; onItemClick: (itemId: string) => void }) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 hover:shadow-md transition-all hover:-translate-y-0.5 duration-200">
        {/* Order card header with ID, item ID, and date */}
        <div className="flex items-center justify-between mb-2 gap-2">
            <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-gray-700">Order #{item.orderId}</span>
                {item.itemId && (
                    <ItemHistoryBadge itemId={item.itemId} onClick={onItemClick} />
                )}
            </div>
            <span className="text-[10px] text-gray-400 flex items-center gap-1 flex-shrink-0">
                <ClockCircleOutlined />
                {moment(item.orderCreationDate).format('DD MMM YY, hh:mm A')}
            </span>
        </div>
        {/* Product image and name row */}
        <div className="flex items-center gap-2 mb-2">
            {item.productImageUrl && (
                <img src={item.productImageUrl} alt="product" className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" />
            )}
            <p className="text-xs text-gray-600 line-clamp-2 leading-snug">{item.productName}</p>
        </div>
        {/* Order status and price tags */}
        <div className="flex items-center gap-1.5 flex-wrap">
            <Tag color="default" className="text-[10px]">{item.paymentMode}</Tag>
            <Tag
                color={item.itemStatus?.includes('RETURN') ? 'red' : item.itemStatus === 'DELIVERED' ? 'green' : 'blue'}
                className="text-[10px]"
            >
                {item.itemStatus}
            </Tag>
            <span className="text-xs font-semibold text-gray-700 ml-auto">{item.price}</span>
        </div>
    </div>
);

// Single interaction event card for the right panel timeline
const InteractionCard = ({ event, onCallClick, onTicketClick }: {
    event: InteractionEvent;
    onCallClick?: (type: 'ivr_call' | 'sahara_call', ucid: string) => void;
    onTicketClick?: (ticketId: string) => void;
}) => {
    const config = TYPE_CONFIG[event.type];
    // Mark ticket cards as open when their status is in the open set
    const isOpen = event.type === 'ticket' && isTicketOpen(event.meta.status);

    // Extract UCID from the event id for call types
    const callUcid = event.type === 'ivr_call'
        ? event.id.replace('ivr-', '')
        : event.type === 'sahara_call'
            ? event.id.replace('sahara-', '')
            : '';

    return (
        <div
            onClick={() => {
                if ((event.type === 'ivr_call' || event.type === 'sahara_call') && onCallClick) {
                    onCallClick(event.type, callUcid);
                }
                // Ticket cards open the ticket detail popup
                if (event.type === 'ticket' && onTicketClick) {
                    onTicketClick(event.title.replace('Ticket #', ''));
                }
            }}
            // Show pointer cursor on all clickable interaction cards
            className={`rounded-xl shadow-sm p-3 transition-all hover:-translate-y-0.5 duration-200 hover:shadow-md cursor-pointer ${isOpen
                ? 'bg-red-50 border border-red-300 border-l-4 border-l-red-500'
                : 'bg-white border border-gray-100'
                }`}
        >
            {/* Card header with type tag, open badge, and timestamp */}
            <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                    <Tag icon={config.icon} color={config.labelColor} className="text-xs">{config.label}</Tag>
                    {/* Red open badge shown only for open tickets */}
                    {isOpen && (
                        <span className="text-[10px] font-semibold text-red-600 bg-red-100 border border-red-300 rounded-full px-1.5 py-0.5 leading-none">
                            Open
                        </span>
                    )}
                </div>
                {/* Timestamp color matches card state — red for open tickets, gray otherwise */}
                <span className={`text-[10px] flex items-center gap-1 flex-shrink-0 ${isOpen ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                    <ClockCircleOutlined />
                    {moment(event.time).format('DD MMM YY, hh:mm A')}
                </span>
            </div>
            {/* Event title — ticket titles open popup, non-ticket titles are plain text */}
            {event.type === 'ticket' ? (
                <button
                    className="text-sm font-semibold text-blue-600 hover:text-blue-800 underline underline-offset-2 mb-0.5 leading-tight text-left"
                >
                    {event.title}
                </button>
            ) : (
                <p className="text-sm font-semibold text-gray-800 mb-0.5 leading-tight">{event.title}</p>
            )}
            <p className="text-xs text-gray-500 mb-2 leading-snug line-clamp-2">{event.subtitle}</p>

            {/* IVR call metadata */}
            {event.type === 'ivr_call' && (
                <div className="flex items-center gap-3 flex-wrap text-xs text-gray-600">
                    {event.meta.agent && (
                        <span className="flex items-center gap-1"><UserOutlined className="text-gray-400" />{event.meta.agent ?? ""}</span>
                    )}
                    {event.meta.duration && (
                        <span className="flex items-center gap-1"><ClockCircleOutlined className="text-gray-400" />{event.meta.duration ?? ""}</span>
                    )}
                    {event.meta.audioUrl && (
                        <Tooltip title="Play Recording">
                            <a href={event.meta.audioUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-purple-600 hover:text-purple-800">
                                <PlayCircleOutlined /> Recording
                            </a>
                        </Tooltip>
                    )}
                </div>
            )}

            {/* Sahara Voice AI call metadata */}
            {event.type === 'sahara_call' && (
                <div className="flex gap-2">
                    <Tag icon={<RobotOutlined />} color="purple" className="text-xs">{event.meta.turnCount} Turns</Tag>
                    <Tag icon={<ThunderboltOutlined />} color="volcano" className="text-xs">{event.meta.toolUseCount} Tools</Tag>
                </div>
            )}

            {/* Ticket metadata with status, category, and agent */}
            {event.type === 'ticket' && (
                <div className="flex flex-wrap gap-1.5 text-xs">
                    <Tag color={getTicketStatusColor(event.meta.status)} className="text-[10px]">
                        {getTicketStatusLabel(event.meta.status)}
                    </Tag>
                    {/* Department shown in bold when derived from source */}
                    {event.meta.department && (
                        <span className="font-bold text-gray-700 text-[10px] bg-gray-100 border border-gray-300 rounded px-1.5 py-0.5 leading-none">
                            {event.meta.department ?? ""}
                        </span>
                    )}
                    {event.meta.category && <Tag className="text-[10px]">{event.meta.category ?? ""}</Tag>}
                    {event.meta.agent && (
                        <span className="flex items-center gap-1 text-gray-500">
                            <UserOutlined />{event.meta.agent ?? ""}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

// Vertical interaction timeline with left spine and event cards
const InteractionList = ({
    events,
    eventRefs,
    onCallClick,
    onTicketClick,
}: {
    events: InteractionEvent[];
    eventRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    onCallClick: (type: 'ivr_call' | 'sahara_call', ucid: string) => void;
    onTicketClick: (ticketId: string) => void;
}) => (
    <div className="relative pl-6">
        {/* Vertical spine line */}
        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" />
        {events.map((event) => {
            const config = TYPE_CONFIG[event.type];
            return (
                // Attach ref so open-ticket strip links can scroll here
                <div
                    key={event.id}
                    className="relative mb-5"
                    ref={(el) => { eventRefs.current[event.id] = el; }}
                >
                    {/* Spine dot icon */}
                    <div
                        className="absolute -left-6 top-3 w-7 h-7 rounded-full flex items-center justify-center text-white shadow ring-2 ring-white text-xs"
                        style={{ backgroundColor: config.color }}
                    >
                        {config.icon}
                    </div>
                    {/* Pass click handlers so call and ticket cards can open detail modals */}
                    <InteractionCard event={event} onCallClick={onCallClick} onTicketClick={onTicketClick} />
                </div>
            );
        })}
    </div>
);

// User summary stats card showing orders, spend, AOV, open tickets, closed tickets, and call counts
const UserInfoCard = ({ userInfo, data, priorityStatus, loadingPriority, totalSpend, aov }: {
    userInfo: any;
    data: any;
    priorityStatus: number | false | null;
    loadingPriority: boolean;
    totalSpend: number;
    aov: number;
}) => {
    // Derive open and closed ticket counts from ticket status
    const allTickets: any[] = data?.tickets || [];
    const openCount = allTickets.filter(t => isTicketOpen(t.status)).length;
    const closedCount = allTickets.length - openCount;

    return (
        <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-2xl border border-blue-100 p-5 mb-6 flex items-center gap-6 flex-wrap shadow-sm">
            <Avatar size={52} className="bg-blue-600 text-white text-xl flex-shrink-0" style={{ backgroundColor: '#2563eb' }}>
                {userInfo?.firstName?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base font-bold text-gray-900">{userInfo?.firstName} {userInfo?.lastName}</h2>
                    {/* Priority customer tag — lazy loaded after user ID resolves */}
                    {loadingPriority && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-gray-100 text-gray-400 border-gray-200 animate-pulse">⭐ …</span>
                    )}
                    {!loadingPriority && priorityStatus === 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-red-100 text-red-700 border-red-300">⭐ P0 Customer</span>
                    )}
                    {!loadingPriority && priorityStatus === 1 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-orange-100 text-orange-700 border-orange-300">🌟 P1 Customer</span>
                    )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                    {userInfo?.phone && userInfo?.id ? (
                        <a
                            href={`/crm/customer/${userInfo.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline font-medium"
                        >
                            {userInfo.phone}
                        </a>
                    ) : (
                        <span>{userInfo?.phone}</span>
                    )}
                    {userInfo?.email ? ` • ${userInfo.email}` : ''}
                </p>
            </div>
            <div className="flex gap-5 flex-wrap items-center">
                {/* Orders stat pill */}
                <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-600">{(data?.orders || []).length}</div>
                    <div className="text-[10px] text-gray-400">Orders</div>
                </div>
                {/* Spend stat */}
                <div className="text-center">
                    <div className="text-2xl font-bold text-gray-800">
                        ₹{totalSpend.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                    <div className="text-[10px] text-gray-400">Total Spend</div>
                </div>
                {/* AOV stat */}
                <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">
                        {aov > 0 ? `₹${aov.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}
                    </div>
                    <div className="text-[10px] text-gray-400">AOV</div>
                </div>
                {/* Open tickets count highlighted in red */}
                <div className="text-center">
                    <div className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-lg bg-red-100 text-red-600 text-[18px] font-bold">
                        {openCount}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5">Open Tickets</div>
                </div>
                {/* Closed tickets count */}
                <div className="text-center">
                    <div className="text-xl font-bold text-gray-400 mt-1">{closedCount}</div>
                    <div className="text-[10px] text-gray-400">Closed Tickets</div>
                </div>
                {/* IVR calls count */}
                <div className="text-center">
                    <div className="text-xl font-bold text-blue-600 mt-1">{(data?.ivr_call_history || []).length}</div>
                    <div className="text-[10px] text-gray-400">IVR Calls</div>
                </div>
                {/* AI calls count */}
                <div className="text-center">
                    <div className="text-xl font-bold text-purple-600 mt-1">{(data?.sahara_call_history || []).length}</div>
                    <div className="text-[10px] text-gray-400">AI Calls</div>
                </div>


            </div>
        </div>
    );
};


// Main Interaction Timeline page component
const InteractionTimeline = () => {
    // Search mode: phone, user_id, or email
    const [searchType, setSearchType] = useState<'phone' | 'user_id' | 'email'>('phone');
    // Item history modal state — null = closed, string = active item ID
    const [activeItemId, setActiveItemId] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // State for IVR call detail popup
    const [activeIvrUcid, setActiveIvrUcid] = useState<string>('');
    // State for Voice AI call detail popup
    const [activeVoiceAiUcid, setActiveVoiceAiUcid] = useState<string>('');
    // State for WhatsApp chat history modal
    const [whatsappVisible, setWhatsappVisible] = useState(false);
    // State for Freshdesk ticket detail popup

    // Priority customer tag — null=not yet checked, false=not priority, 0/1=priority level
    const [priorityStatus, setPriorityStatus] = useState<number | false | null>(null);
    const [loadingPriority, setLoadingPriority] = useState(false);

    // Config for each search mode: query param, icon, placeholder
    const SEARCH_CONFIG = {
        phone: {
            param: 'phone',
            icon: <PhoneOutlined className="text-gray-400" />,
            placeholder: 'Enter phone number...',
            label: 'Phone',
        },
        user_id: {
            param: 'user_id',
            icon: <IdcardOutlined className="text-gray-400" />,
            placeholder: 'Enter user ID...',
            label: 'User ID',
        },
        email: {
            param: 'email',
            icon: <MailOutlined className="text-gray-400" />,
            placeholder: 'Enter email address...',
            label: 'Email',
        },
    };

    const fetchTimeline = async (value: string, type: 'phone' | 'user_id' | 'email' = searchType) => {
        if (!value.trim()) return;
        setLoading(true);
        setError('');
        try {
            const param = SEARCH_CONFIG[type].param;
            const res = await axiosInstance.get(`${apis.CRM_USER_TIMELINE}?${param}=${value.trim()}`);
            setData(res.data);

            // Update URL with search parameters for sharing
            const url = new URL(window.location.href);
            url.searchParams.set(param === 'phone' ? 'phone_number' : param, value.trim());
            // Clear other search params
            if (param !== 'phone') url.searchParams.delete('phone_number');
            if (param !== 'user_id') url.searchParams.delete('user_id');
            if (param !== 'email') url.searchParams.delete('email');
            window.history.replaceState({}, '', url.toString());
        } catch {
            setError('Failed to load timeline. Please check the search value and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Auto-search on mount if any supported query param is found in the URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const phoneParam = params.get('phone_number');
        const userIdParam = params.get('user_id');
        const emailParam = params.get('email');
        if (phoneParam) {
            setSearchType('phone');
            setQuery(phoneParam);
            fetchTimeline(phoneParam, 'phone');
        } else if (userIdParam) {
            setSearchType('user_id');
            setQuery(userIdParam);
            fetchTimeline(userIdParam, 'user_id');
        } else if (emailParam) {
            setSearchType('email');
            setQuery(emailParam);
            fetchTimeline(emailParam, 'email');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Lazy-load priority status whenever a new user_info.id is present in the timeline data
    useEffect(() => {
        const userId = data?.user_info?.id;
        if (!userId) { setPriorityStatus(null); return; }
        setLoadingPriority(true);
        axiosInstance.post(apis.CHECK_CUSTOMER_PRIORITY_ORDERS, {
            orderId: [], pageNumber: 1, pageCount: 50, itemId: [], itemStatus: [],
            supplierId: [], customerId: [userId], email: '', phone: '', awb: [],
            forwardAwb: [], shipperId: [], returnId: [], productRef: '', shipmentId: [],
            clusterList: [], categoryList: [], countryCode: 'IN',
        })
            // Empty data array means not a priority customer
            .then(res => {
                const arr: any[] = res.data?.data || [];
                if (arr.length === 0) {
                    setPriorityStatus(false);
                } else {
                    // Use the lowest priorityStatus (P0 > P1) from the response
                    setPriorityStatus(Math.min(...arr.map((d: any) => d.priorityStatus)));
                }
            })
            .catch(() => setPriorityStatus(false))
            .finally(() => setLoadingPriority(false));
    }, [data?.user_info?.id]);

    // Refs map keyed by event ID for scroll-to-card behaviour
    const eventRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Dispatch click from a call card to the right modal
    const handleCallClick = (type: 'ivr_call' | 'sahara_call', ucid: string) => {
        if (type === 'ivr_call') {
            setActiveIvrUcid(ucid);
        } else {
            setActiveVoiceAiUcid(ucid);
        }
    };

    // Navigate to the ticket detail page in a new tab
    const handleTicketClick = (ticketId: string) => {
        window.open(`/crm/freshdesk-tickets/${ticketId}`, '_blank');
    };

    // Derive orders and interactions from loaded data
    const orders = data ? buildOrders(data) : [];
    const interactions = data ? buildInteractions(data) : [];

    // Calculate spend metrics from orders array
    const totalSpend = orders
        .filter(o => o.itemStatus === 'DELIVERED')
        .reduce((s, o) => s + Number(o.price.replace('₹', '').replace(/,/g, '') || 0), 0);
    const deliveredCount = orders.filter(o => o.itemStatus === 'DELIVERED').length;
    const aov = deliveredCount > 0 ? totalSpend / deliveredCount : 0;

    return (
        <StitchLayout>
            {/* Full-width content area */}
            <div className="max-w-7xl mx-auto px-4">
                {/* Smart search bar with segmented type toggle and dynamic input */}
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-6">
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900 mb-0.5">Interaction Timeline</h1>
                        <p className="text-sm text-gray-500">Search by phone, user ID, or email</p>
                    </div>
                    {/* Search type + input + button grouped */}
                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                        {/* Segmented control to switch search mode */}
                        <Segmented
                            value={searchType}
                            onChange={(val) => {
                                setSearchType(val as any);
                                setQuery('');
                                setData(null);
                            }}
                            options={[
                                { label: <span className="flex items-center gap-1 px-1"><PhoneOutlined />Phone</span>, value: 'phone' },
                                { label: <span className="flex items-center gap-1 px-1"><IdcardOutlined />User ID</span>, value: 'user_id' },
                                { label: <span className="flex items-center gap-1 px-1"><MailOutlined />Email</span>, value: 'email' },
                            ]}
                            className="self-start"
                        />
                        {/* Dynamic input and search button */}
                        <div className="flex gap-2">
                            <Input
                                size="large"
                                prefix={SEARCH_CONFIG[searchType].icon}
                                placeholder={SEARCH_CONFIG[searchType].placeholder}
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                onPressEnter={() => fetchTimeline(query)}
                                allowClear
                                className="sm:w-72"
                            />
                            <Button size="large" type="primary" icon={<SearchOutlined />} onClick={() => fetchTimeline(query)} loading={loading}>
                                Search
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Loading state spinner */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <Spin size="large" />
                        <p className="text-gray-400 text-sm">Loading customer timeline...</p>
                    </div>
                )}

                {/* Error state message */}
                {error && <div className="text-red-500 text-center py-8">{error}</div>}

                {/* Two-panel layout after data is loaded */}
                {!loading && data && (
                    <>
                        {/* User info card + WhatsApp CTA */}
                        <div className="flex items-start gap-3 mb-6">
                            <div className="flex-1 min-w-0">
                                <UserInfoCard userInfo={data.user_info} data={data} priorityStatus={priorityStatus} loadingPriority={loadingPriority} totalSpend={totalSpend} aov={aov} />
                            </div>
                            {/* WhatsApp chat history icon CTA */}
                            {data.user_info?.phone && (
                                <button
                                    onClick={() => setWhatsappVisible(true)}
                                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
                                    style={{ background: '#25D366' }}
                                    title="WhatsApp Chat History"
                                >
                                    <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Open tickets strip — shows clickable ticket IDs that scroll to their card */}
                        {interactions.filter(e => e.type === 'ticket' && isTicketOpen(e.meta.status)).length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 mb-5">
                                <span className="text-xs font-semibold text-red-600 flex-shrink-0">🔴 Open Tickets:</span>
                                {interactions
                                    .filter(e => e.type === 'ticket' && isTicketOpen(e.meta.status))
                                    .map(e => (
                                        // Clickable ticket ID pill — opens ticket detail popup
                                        <button
                                            key={e.id}
                                            onClick={() => window.open(`/crm/freshdesk-tickets/${e.title.replace('Ticket #', '')}`, '_blank')}
                                            className="text-xs font-semibold text-red-700 underline underline-offset-2 hover:text-red-900 bg-white border border-red-300 rounded-full px-2.5 py-0.5 transition-colors hover:bg-red-100 cursor-pointer"
                                        >
                                            #{e.title.replace('Ticket #', '')}
                                        </button>
                                    ))
                                }
                            </div>
                        )}

                        <div className="flex gap-6 items-start">
                            {/* Left panel – Orders list */}
                            <div className="w-[38%] flex-shrink-0">
                                <div className="flex items-center gap-2 mb-4">
                                    <ShoppingOutlined className="text-emerald-600" />
                                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                                        Orders
                                    </h2>
                                    <span className="ml-auto text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                                        {orders.length}
                                    </span>
                                </div>
                                {orders.length > 0 && (() => {
                                    const s = (o: any) => (o.itemStatus || '').toUpperCase();
                                    const groups = [
                                        { label: 'Accepted', color: 'text-gray-600', count: orders.filter(o => ['NEW', 'ASSIST_CONVERT', 'VERIFICATION_PENDING', 'CUSTOMER_EXCHANGE_VERIFIED', 'VERIFIED'].includes(s(o))).length },
                                        { label: 'Awaiting Payment', color: 'text-yellow-600', count: orders.filter(o => ['PAYMENT_PENDING', 'PAYMENT_GATEWAY_PENDING'].includes(s(o))).length },
                                        { label: 'Payment Failed', color: 'text-orange-600', count: orders.filter(o => s(o) === 'PAYMENT_FAILED').length },
                                        { label: 'Processing', color: 'text-indigo-600', count: orders.filter(o => ['SUPPLIER_ASSIGNED', 'FAILED_SUPPLIER', 'PICKUP_SCHEDULED', 'LOGISTIC_PICKUP_ACTIVATE', 'OUT_FOR_PICKUP', 'PICKUP_PENDING', 'PICKUP_DONE', 'INVENTORY_PICKUP', 'SUPPLIER_EXCHANGE_REQUESTED', 'SUPPLIER_OUT_FOR_EXCHANGE', 'QC_FAIL', 'QC_PASS', 'DROPSHIP_DETECTED', 'DROPSHIP_ACTIVATED', 'SHIPMENT_CREATED'].includes(s(o))).length },
                                        { label: 'Packed', color: 'text-teal-600', count: orders.filter(o => ['PACKED', 'MANIFEST_CREATED'].includes(s(o))).length },
                                        { label: 'Shipped', color: 'text-emerald-600', count: orders.filter(o => s(o) === 'SHIPPED').length },
                                        { label: 'Delivered', color: 'text-green-600', count: orders.filter(o => ['DELIVERED', 'CLOSED'].includes(s(o))).length },
                                        { label: 'Return Rejected', color: 'text-orange-500', count: orders.filter(o => ['RETURN_NOT_REQUIRED', 'REQUEST_REJECTED'].includes(s(o))).length },
                                        { label: 'Exchange Requested', color: 'text-amber-600', count: orders.filter(o => s(o) === 'CUSTOMER_EXCHANGE_REQUESTED').length },
                                        { label: 'Exchange Approved', color: 'text-amber-700', count: orders.filter(o => ['RVP_RAISED', 'EXCHANGE_APPROVED', 'CUSTOMER_EXCHANGE_APPROVED'].includes(s(o))).length },
                                        { label: 'Exchange Picked', color: 'text-pink-600', count: orders.filter(o => ['RETURN_PICKED', 'WAREHOUSE_RETURN', 'RTV_DONE', 'RTV_DENIED', 'RTV_INITIATED', 'RETURN_QCPASS', 'RETURN_QCFAIL', 'TAX_PO', 'INVENTORIZE'].includes(s(o))).length },
                                        { label: 'Return Requested', color: 'text-orange-600', count: orders.filter(o => s(o) === 'RETURN_REQUESTED').length },
                                        { label: 'Return Approved', color: 'text-rose-600', count: orders.filter(o => ['RVP_RAISED', 'RETURN_APPROVED'].includes(s(o))).length },
                                        { label: 'Return Done', color: 'text-red-600', count: orders.filter(o => ['WAREHOUSE_RETURN', 'RTV_DONE', 'RTV_DENIED', 'RTV_INITIATED', 'RETURN_QCPASS', 'RETURN_QCFAIL', 'TAX_PO', 'INVENTORIZE'].includes(s(o))).length },
                                        { label: 'Cancelled', color: 'text-red-500', count: orders.filter(o => ['RETURN_TO_ORIGIN', 'CUSTOMER_CANCEL', 'ADMIN_CANCEL', 'DUMMY_ORDER'].includes(s(o))).length },
                                    ].filter(g => g.count > 0);
                                    if (groups.length === 0) return null;
                                    return (
                                        <div className="mb-4 text-[10px] font-semibold bg-gray-50 border border-gray-100 rounded-xl p-3 flex flex-wrap gap-x-4 gap-y-2">
                                            {groups.map(g => (
                                                <div key={g.label} className="flex items-center gap-1.5">
                                                    <span className={g.color}>{g.label}</span>
                                                    <span className="text-gray-900 border border-gray-200 bg-white rounded-md px-1">{g.count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                                {orders.length === 0
                                    ? <Empty description="No orders found" className="py-10" />
                                    : (
                                        <div className="flex flex-col gap-3">
                                            {orders.map(item => (
                                                <OrderCard key={item.itemId} item={item} onItemClick={setActiveItemId} />
                                            ))}
                                        </div>
                                    )
                                }
                            </div>

                            {/* Vertical divider between panels */}
                            <div className="w-px bg-gray-200 self-stretch flex-shrink-0" />

                            {/* Right panel – Interaction timeline (calls + tickets) */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-4">
                                    <CustomerServiceOutlined className="text-amber-500" />
                                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                                        Interactions
                                    </h2>
                                    <span className="ml-auto text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                                        {interactions.length}
                                    </span>
                                </div>
                                {interactions.length === 0
                                    ? <Empty description="No interactions found" className="py-10" />
                                    : <InteractionList events={interactions} eventRefs={eventRefs} onCallClick={handleCallClick} onTicketClick={handleTicketClick} />
                                }
                            </div>
                        </div>
                    </>
                )}

                {/* Default empty state before first search */}
                {!loading && !data && !error && (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
                        <PhoneOutlined style={{ fontSize: 48 }} />
                        <p className="text-sm">Enter a phone number to load interaction history</p>
                    </div>
                )}
            </div>

            {/* WhatsApp chat history modal */}
            <WhatsAppConversationModal
                visible={whatsappVisible}
                phone={data?.user_info?.phone?.replace(/^\+/, '').replace(/^0/, '')}
                onClose={() => setWhatsappVisible(false)}
            />

            {/* IVR call detail modal triggered from timeline card click */}
            <OzonetelCallDetails
                visible={!!activeIvrUcid}
                ucid={activeIvrUcid}
                onClose={() => setActiveIvrUcid('')}
            />

            {/* Item history modal triggered from item ID badge click */}
            <ItemHistoryModal itemId={activeItemId} onClose={() => setActiveItemId(null)} />

            {/* Voice AI call detail modal triggered from timeline card click */}
            <SaharaCallDetails
                open={!!activeVoiceAiUcid}
                ucid={activeVoiceAiUcid}
                onClose={() => setActiveVoiceAiUcid('')}
            />

        </StitchLayout>
    );
};

export default InteractionTimeline;
